import React from 'react';
import { FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { getShortLabel, getCurrencySettings } from '../utils/currencyHelper';
import { formatSourceUpdate, isCurrencyStale, isIstanbulMarketOpen } from '../utils/marketStatus';
import { getAssetDisplayName } from '../utils/assetDisplayNames';

const CurrencyCard = ({ currency }) => {
  // Safe Access: Ensure currency exists and values are numbers
  if (!currency) return null;

  const hasPreviousClose = typeof currency.previousClose === 'number' && Number.isFinite(currency.previousClose) && currency.previousClose > 0;
  const change = (hasPreviousClose && typeof currency.change === 'number' && !isNaN(currency.change)) ? currency.change : null;
  const changePercent = (hasPreviousClose && typeof currency.changePercent === 'number' && !isNaN(currency.changePercent)) ? currency.changePercent : null;
  const buyPrice = typeof currency.buyPrice === 'number' && currency.buyPrice > 0 ? currency.buyPrice : null;
  const sellPrice = typeof currency.sellPrice === 'number' && currency.sellPrice > 0 ? currency.sellPrice : null;

  const isPositive = change !== null && change >= 0;
  const displayName = getAssetDisplayName(currency);

  // Get Smart Settings
  const settings = getCurrencySettings(currency.code, buyPrice);

  // Piyasa açık/kapalı kontrolü (09:00 - 18:00 arası açık)
  const isMarketOpen = isIstanbulMarketOpen();
  const isStale = isCurrencyStale(currency);
  const isLive = isMarketOpen && currency.source === 'TRUNCGIL' && !isStale;

  // Border Color Logic
  const borderColorClass = change === null
    ? 'border-l-gray-300'
    : !isLive
      ? 'border-l-slate-400' // Neutral when closed
      : isPositive
        ? 'border-l-emerald-500' // Rising
        : 'border-l-rose-500'; // Falling

  // Helper to format number with specific decimals
  const formatNumber = (val) => {
    if (val === undefined || val === null || val === 0) return '—';
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: settings.decimals,
      maximumFractionDigits: settings.decimals
    }).format(val);
  };

  return (
    <div className={`bg-white dark:bg-slate-900 rounded-2xl shadow-soft hover:shadow-xl hover:-translate-y-1 transition-all duration-300 ease-in-out border border-gray-100 dark:border-white/10 hover:border-indigo-300 dark:hover:border-indigo-500/50 group border-l-4 ${borderColorClass} overflow-hidden flex flex-col h-full`}>
      <div className="flex justify-between items-start gap-2 mb-5 p-4 sm:p-5 pb-0">
        <div className="flex-1 min-w-0 pr-3">
          <h3
            className="h-14 line-clamp-2 text-base sm:text-lg leading-7 font-bold text-slate-800 dark:text-white tracking-tight"
            title={displayName}
          >
            {displayName}
          </h3>
          <div className="flex items-center space-x-2 mt-1.5">
            <span className="text-xs font-bold uppercase text-primary-600 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/40 px-2.5 py-1 rounded-lg border border-primary-100 dark:border-white/5">{getShortLabel(currency.code)}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${isLive ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 border border-emerald-100 dark:border-white/5' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-white/5'}`}>
              {currency.source === 'TCMB' ? 'Gösterge' : isStale ? 'Eski veri' : isMarketOpen ? 'Güncel' : 'Kapalı'}
            </span>
          </div>
        </div>
        <div className={`flex-shrink-0 flex flex-col items-end p-2 rounded-xl border ${change === null ? 'bg-gray-50/50 dark:bg-gray-800 border-gray-100 dark:border-gray-700 text-gray-500' : !isLive ? 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400' : isPositive ? 'bg-emerald-50/50 dark:bg-emerald-500/10 border-emerald-100 dark:border-emerald-500/20 text-emerald-600 dark:text-emerald-400' : 'bg-rose-50/50 dark:bg-rose-500/10 border-rose-100 dark:border-rose-500/20 text-rose-600 dark:text-rose-400'}`}>
          {!isLive && change !== null && (
            <span className="text-[9px] uppercase font-semibold text-slate-400 dark:text-slate-500 mb-0.5 leading-none">Son Değişim</span>
          )}
          <div className="flex items-center space-x-1">
            {isLive && change !== null && (isPositive ? <FaArrowUp className="text-xs" /> : <FaArrowDown className="text-xs" />)}
            <span className="font-bold text-base leading-none">{changePercent !== null ? `${(changePercent > 0 ? '+' : '')}${changePercent.toFixed(2)}%` : '—'}</span>
          </div>
        </div>
      </div>

      <div className="space-y-3 p-4 sm:p-5 pt-2">
        <div className="flex justify-between items-center p-3 bg-slate-50 dark:bg-slate-950/50 rounded-xl border border-slate-100 dark:border-white/5 group-hover:border-slate-200 dark:group-hover:border-white/10 transition-colors">
          <span className="text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide">Alış</span>
          <div className="flex items-center gap-1 text-base sm:text-lg font-bold text-slate-700 dark:text-white font-mono tracking-tight min-w-0">
            <span className="font-sans text-base opacity-70 dark:text-gray-500 inline-block">{settings.symbol}</span>
            <span>{formatNumber(buyPrice)}</span>
          </div>
        </div>
        <div className="flex justify-between items-center p-3 bg-slate-50 dark:bg-slate-950/50 rounded-xl border border-slate-100 dark:border-white/5 group-hover:border-slate-200 dark:group-hover:border-white/10 transition-colors">
          <span className="text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide">Satış</span>
          <div className="flex items-center gap-1 text-base sm:text-lg font-bold text-slate-700 dark:text-white font-mono tracking-tight min-w-0">
            <span className="font-sans text-base opacity-70 dark:text-gray-500 inline-block">{settings.symbol}</span>
            <span>{formatNumber(sellPrice)}</span>
          </div>
        </div>
      </div>

      <div className="flex-1" />
      <div className="p-3 sm:p-4 pt-3 pb-3 border-t border-gray-100 dark:border-white/10 flex justify-between items-center gap-2 bg-gray-50/50 dark:bg-white/5 mx-4 sm:mx-5 mb-4 sm:mb-5 mt-auto rounded-b-xl min-w-0">
        <div className="flex flex-col">
          <span className="text-xs text-gray-500 dark:text-gray-400 font-medium mb-1">
            {isLive ? 'Günlük Değişim' : 'Son Değişim'}
          </span>
          <span className="text-[10px] text-gray-400 dark:text-gray-500/80 flex items-center">
            {currency.source || 'Kaynak yok'} · {formatSourceUpdate(currency)}
          </span>
        </div>
        <span className={`text-sm font-bold flex items-center gap-1 ${change === null ? 'text-gray-400' : (!isLive) ? 'text-slate-500 dark:text-slate-400' : isPositive ? 'text-emerald-500 dark:text-emerald-400' : 'text-rose-500 dark:text-rose-400'}`}>
          <span className="text-xs opacity-70 inline-block">{settings.symbol}</span>
          <span>{change !== null && change > 0 ? '+' : ''}{formatNumber(change)}</span>
        </span>
      </div>
    </div>
  );
};

export default CurrencyCard;
